import { ReactNode, useCallback, useMemo, useState } from 'react';
import { ComponentRendering, RouteData } from '@sitecore-jss/sitecore-jss-nextjs';

import { getSharedArray } from '../../../../getters';
import { TitledSitecoreItem } from '../../../../types';
import { MajorLink, MenuItem, SubmenuItem } from '../../MegaMenuTypes';
import { getTopMenuItemNameFromSCMenuItem } from '../../MegaMenuUtils';
import { HeaderContext } from './header.context';

interface Props {
  children?: ReactNode;
  fields?: { MenuItems?: MenuItem[]; MenuItemsShared?: MenuItem[] };
  currentLocale?: string;
  mediaHost?: string;
  route: RouteData | undefined;
  onLanguageSelect?: () => void;
  myJabraUrl?: string;
}

export const HeaderProvider = ({
  fields,
  children,
  currentLocale = 'en',
  route,
  mediaHost = '',
  onLanguageSelect,
  myJabraUrl = '',
}: Props): JSX.Element => {
  const [activeItemId, setActiveItemId] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [shouldFocus, setShouldFocus] = useState(false);
  const [activeMajorLink, setActiveMajorLink] = useState<TitledSitecoreItem<MajorLink> | null>(
    null
  );
  const data = (route?.placeholders['jss-main'] as ComponentRendering[]).find(
    (component) => (component as ComponentRendering).componentName.toLowerCase() === 'submenu'
  );

  const submenuItems = data
    ? getSharedArray(
        (data as ComponentRendering).fields as unknown as {
          items: SubmenuItem[];
        },
        'items'
      )
    : null;

  const activeSubmenuItem =
    activeItemId && submenuItems
      ? submenuItems.find((item) => item.displayName.toLowerCase() === activeItemId.toLowerCase())
      : undefined;

  const onMenuItemClick = useCallback(
    (menuItem: MenuItem) => {
      const name = getTopMenuItemNameFromSCMenuItem(menuItem);
      const shouldClose = activeItemId === name;

      setActiveItemId(shouldClose ? '' : name);
      setIsMobileMenuOpen(!shouldClose);

      setShouldFocus(false);
    },
    [activeItemId]
  );
  const openMenuItem = useCallback((menuItem: MenuItem) => {
    const name = getTopMenuItemNameFromSCMenuItem(menuItem);
    setActiveItemId(name);
    setIsMobileMenuOpen(true);
    setShouldFocus(true);
  }, []);

  const onMobileMenuClick = useCallback(() => {
    if (isMobileMenuOpen) {
      setActiveItemId('');
    }

    setIsMobileMenuOpen((isOpen) => !isOpen);
  }, [isMobileMenuOpen]);

  const closeMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
    setActiveItemId('');
    setShouldFocus(false);
  }, []);

  const onBackClick = useCallback(() => {
    if (activeItemId) {
      setActiveItemId('');
      setShouldFocus(false);
    } else {
      closeMenu();
    }
  }, [activeItemId, closeMenu]);

  const onMajorCategoryHover = useCallback((majorLink: TitledSitecoreItem<MajorLink>) => {
    setActiveMajorLink(majorLink);
  }, []);

  const menuItems = useMemo(
    () =>
      (fields?.MenuItemsShared && fields?.MenuItems && getSharedArray(fields, 'MenuItems')) || [],
    [fields]
  );

  return (
    <HeaderContext.Provider
      value={useMemo(
        () => ({
          activeItemId,
          isMobileMenuOpen,
          menuItems,
          shouldFocus,
          onMenuItemClick,
          onMobileMenuClick,
          activeSubmenuItem,
          onBackClick,
          activeMajorLink,
          onMajorCategoryHover,
          closeMenu,
          openMenuItem,
          onLanguageSelect,
          currentLocale,
          mediaHost,
          myJabraUrl,
          route,
        }),
        [
          activeItemId,
          isMobileMenuOpen,
          menuItems,
          shouldFocus,
          onMenuItemClick,
          onMobileMenuClick,
          activeSubmenuItem,
          onBackClick,
          activeMajorLink,
          onMajorCategoryHover,
          closeMenu,
          openMenuItem,
          onLanguageSelect,
          currentLocale,
          mediaHost,
          myJabraUrl,
          route,
        ]
      )}
    >
      {children}
    </HeaderContext.Provider>
  );
};
